import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    Alert,
    Badge,
    Box,
    Button,
    Card,
    Divider,
    Grid,
    Group,
    Loader,
    NumberInput,
    SegmentedControl,
    Stack,
    Switch,
    Text,
    Title,
} from "@mantine/core";
import {
    IconAlertCircle,
    IconCheck,
    IconDeviceFloppy,
    IconInfoCircle,
    IconShieldLock,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { DateInput } from "@mantine/dates";
import { doc, serverTimestamp, setDoc, Timestamp } from "firebase/firestore";
import { useFirestore } from "../../../helpers/firebaseContext";
import { CramConfig, CramMode } from "../../../types";

interface Props {
    config?: CramConfig;
    snapshotVersion?: string;
    loading: boolean;
    // When false (non-admin CRAM user) the tab is read-only: every input and
    // the Save button are disabled. Defaults to true for safety.
    canEdit?: boolean;
}

interface FormState {
    mode: CramMode;
    weights: CramConfig["weights"];
    buckets: CramConfig["buckets"];
    expiry: CramConfig["expiry"];
    audience: CramConfig["audience"];
    activationDate: Date | null;
}

// The activation date is a calendar day. The picker may hand back a Date or a
// "YYYY-MM-DD" string depending on the Mantine version; a plain `new Date(str)`
// parses date-only strings as UTC midnight, which stores/renders as the wrong
// day in non-UTC timezones. Pin it to LOCAL midnight of the chosen calendar
// date so the stored instant matches what the admin picked everywhere.
function toLocalMidnight(value: string | Date | null): Date | null {
    if (!value) return null;
    if (typeof value === "string") {
        const [y, m, d] = value.split("-").map(Number);
        if (y && m && d) return new Date(y, m - 1, d);
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime())
            ? null
            : new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    }
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}

// config/cram.activationDate can be a Firestore Timestamp, an ISO / date
// string, or epoch-millis. Normalise it to a Date for the picker.
function parseActivationDate(raw: CramConfig["activationDate"]): Date | null {
    if (raw === null || typeof raw === "undefined" || raw === "") return null;
    if (typeof raw === "number") return Number.isFinite(raw) ? new Date(raw) : null;
    if (typeof (raw as any)?.toDate === "function") return (raw as any).toDate();
    const parsed = new Date(raw as any);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function configToForm(c: CramConfig): FormState {
    return {
        // Fall back to the legacy audience.enabled boolean when mode is absent.
        mode: c.mode ?? (c.audience?.enabled ? "live" : "off"),
        weights: { ...c.weights },
        buckets: {
            low: { ...c.buckets.low },
            medium: { ...c.buckets.medium },
            high: { ...c.buckets.high },
            prohibited: { ...c.buckets.prohibited },
        },
        expiry: { ...c.expiry },
        audience: { ...c.audience },
        activationDate: parseActivationDate(c.activationDate),
    };
}

// Warn if any pair of buckets overlaps. The seeded shape uses 0.01 gaps
// (e.g. Low max 2.0, Medium min 2.01); the engine buckets by each band's
// `min` (highest first), so gap-less ranges are also fine, but overlapping
// ranges produce ambiguous ratings. The 1e-9 below is just a float
// tolerance for "touching" boundaries (a.max === b.min).
function detectBucketOverlap(buckets: CramConfig["buckets"]): string | null {
    const ordered = [
        { name: "Low", ...buckets.low },
        { name: "Medium", ...buckets.medium },
        { name: "High", ...buckets.high },
        { name: "Prohibited", ...buckets.prohibited },
    ];
    for (let i = 0; i < ordered.length; i++) {
        const a = ordered[i];
        if (a.min > a.max) {
            return `${a.name} min (${a.min}) is greater than its max (${a.max}).`;
        }
        for (let j = i + 1; j < ordered.length; j++) {
            const b = ordered[j];
            if (a.max >= b.min && a.min <= b.max) {
                if (Math.abs(a.max - b.min) < 1e-9) continue;
                return `${a.name} and ${b.name} ranges overlap (${a.min}–${a.max} vs ${b.min}–${b.max}).`;
            }
        }
    }
    return null;
}

const CramSettingsTab: React.FC<Props> = ({
    config: initialConfig,
    snapshotVersion,
    loading,
    canEdit = true,
}) => {
    const db = useFirestore();
    const [form, setForm] = useState<FormState | null>(null);
    const [saving, setSaving] = useState(false);

    // Serialized snapshot of the config we last adopted into the form.
    // Used to tell apart "the user has unsaved local edits" from "the form
    // simply mirrors the server", so a concurrent server-side change (the
    // parent's live onSnapshot) doesn't silently clobber in-progress edits.
    // Mirrors the dirtyRef approach in CramListEditor.
    const lastSyncedRef = useRef<string | null>(null);

    // Updated during render (not in an effect) so a snapshot arriving between
    // a setForm call and the post-render effect can't observe a stale value.
    const dirtyRef = useRef(false);
    dirtyRef.current =
        form != null &&
        lastSyncedRef.current != null &&
        JSON.stringify(form) !== lastSyncedRef.current;

    // Mirror live config into the form on first load, and on later server
    // pushes only when the user has no unsaved edits.
    useEffect(() => {
        if (!initialConfig) return;
        const firstLoad = lastSyncedRef.current == null;
        if (firstLoad || !dirtyRef.current) {
            const next = configToForm(initialConfig);
            lastSyncedRef.current = JSON.stringify(next);
            setForm(next);
        }
    }, [initialConfig]);

    // `snapshotVersion` comes straight from the parent's `config/cram`
    // listener and already stays fresh after list edits in the other tab,
    // so we use the prop directly instead of opening a second listener.

    const overlap = useMemo(
        () => (form ? detectBucketOverlap(form.buckets) : null),
        [form]
    );

    const dirty = useMemo(() => {
        if (!form || !initialConfig) return false;
        return JSON.stringify(form) !== JSON.stringify(configToForm(initialConfig));
    }, [form, initialConfig]);

    const handleSave = useCallback(async () => {
        if (!form) return;
        if (overlap) {
            notifications.show({
                title: "Cannot save",
                message: overlap,
                color: "red",
                icon: <IconAlertCircle size={16} />,
            });
            return;
        }
        setSaving(true);
        try {
            await setDoc(
                doc(db, "config", "cram"),
                {
                    mode: form.mode,
                    weights: form.weights,
                    buckets: form.buckets,
                    expiry: form.expiry,
                    activationDate: form.activationDate
                        ? Timestamp.fromDate(form.activationDate)
                        : null,
                    // Keep the legacy boolean in sync for any reader that
                    // hasn't moved to `mode` yet.
                    audience: {
                        ...form.audience,
                        enabled: form.mode !== "off",
                    },
                    updatedAt: serverTimestamp(),
                },
                { merge: true }
            );
            // The saved form is now the baseline — clear the dirty flag so a
            // subsequent server snapshot (incl. our own write echoing back)
            // is treated as in-sync and future server pushes are adopted.
            lastSyncedRef.current = JSON.stringify(form);
            notifications.show({
                title: "Saved",
                message: "CRAM settings updated.",
                color: "green",
                icon: <IconCheck size={16} />,
                autoClose: 1500,
            });
        } catch (err: any) {
            console.error("[CRAM] settings save error:", err);
            notifications.show({
                title: "Save failed",
                message: err?.message ?? "Unknown error",
                color: "red",
                icon: <IconAlertCircle size={16} />,
            });
        } finally {
            setSaving(false);
        }
    }, [db, form, overlap]);

    if (loading || !form) {
        return (
            <Group justify="center" py="xl">
                <Loader size="sm" />
            </Group>
        );
    }

    const update = <K extends "weights" | "expiry" | "audience">(
        key: K,
        patch: Partial<FormState[K]>
    ) => {
        setForm((prev) =>
            prev ? { ...prev, [key]: { ...prev[key], ...patch } } : prev
        );
    };

    const updateBucket = (
        key: keyof FormState["buckets"],
        patch: Partial<{ min: number; max: number }>
    ) => {
        setForm((prev) =>
            prev
                ? {
                    ...prev,
                    buckets: {
                        ...prev.buckets,
                        [key]: { ...prev.buckets[key], ...patch },
                    },
                }
                : prev
        );
    };

    return (
        // Native fieldset[disabled] reliably disables every nested form control
        // (Mantine inputs / switches / segmented control / buttons render real
        // native controls), giving a single read-only switch for non-admins.
        <Box
            component="fieldset"
            disabled={!canEdit}
            style={{ border: 0, padding: 0, margin: 0, minInlineSize: "auto" }}
        >
        <Stack gap="md">
            {!canEdit && (
                <Alert
                    icon={<IconShieldLock size={16} />}
                    color="blue"
                    variant="light"
                >
                    These settings are view-only. Only admin users can change the
                    rollout mode, weights, buckets, expiry, and audience flags.
                    You can still edit the CRAM lists from the other tabs.
                </Alert>
            )}
            <Card withBorder radius="sm" p="md">
                <Group justify="space-between" mb="xs">
                    <Box>
                        <Title order={5}>Category Weights</Title>
                        <Text size="xs" c="dimmed">
                            Each category contributes its weighted MAX to the
                            total score (weighted sum). A Direct High hit takes
                            100% of the weight — the score becomes the direct
                            high score alone; otherwise the others share 0.25.
                        </Text>
                    </Box>
                </Group>
                <Divider mb="sm" />
                <Grid gap="xs">
                    <Grid.Col span={{ base: 6, md: 2.4 }}>
                        <NumberInput
                            label="Customer Type"
                            min={0}
                            max={10}
                            step={0.05}
                            decimalScale={4}
                            value={form.weights.customerType}
                            onChange={(v) =>
                                update("weights", {
                                    customerType: Number(v),
                                })
                            }
                        />
                    </Grid.Col>
                    <Grid.Col span={{ base: 6, md: 2.4 }}>
                        <NumberInput
                            label="Geography"
                            min={0}
                            max={10}
                            step={0.05}
                            decimalScale={4}
                            value={form.weights.geography}
                            onChange={(v) =>
                                update("weights", { geography: Number(v) })
                            }
                        />
                    </Grid.Col>
                    <Grid.Col span={{ base: 6, md: 2.4 }}>
                        <NumberInput
                            label="Products & Services"
                            min={0}
                            max={10}
                            step={0.05}
                            decimalScale={4}
                            value={form.weights.productsServices}
                            onChange={(v) =>
                                update("weights", {
                                    productsServices: Number(v),
                                })
                            }
                        />
                    </Grid.Col>
                    <Grid.Col span={{ base: 6, md: 2.4 }}>
                        <NumberInput
                            label="Industry Type"
                            min={0}
                            max={10}
                            step={0.05}
                            decimalScale={4}
                            value={form.weights.industryType}
                            onChange={(v) =>
                                update("weights", {
                                    industryType: Number(v),
                                })
                            }
                        />
                    </Grid.Col>
                    <Grid.Col span={{ base: 6, md: 2.4 }}>
                        <NumberInput
                            label="Direct High"
                            min={0}
                            max={10}
                            step={0.1}
                            decimalScale={4}
                            value={form.weights.directHigh}
                            onChange={(v) =>
                                update("weights", { directHigh: Number(v) })
                            }
                        />
                    </Grid.Col>
                </Grid>
            </Card>

            <Card withBorder radius="sm" p="md">
                <Group justify="space-between" mb="xs">
                    <Box>
                        <Title order={5}>Rating Buckets</Title>
                        <Text size="xs" c="dimmed">
                            Score ranges (weighted sum) that map a score to a
                            final rating. Bucket boundaries are inclusive at the
                            min and exclusive at the next bucket's min.
                        </Text>
                    </Box>
                </Group>
                <Divider mb="sm" />
                {overlap && (
                    <Alert
                        icon={<IconAlertCircle size={16} />}
                        color="red"
                        variant="light"
                        mb="sm"
                    >
                        {overlap}
                    </Alert>
                )}
                {(
                    [
                        { key: "low", label: "Low", color: "green" },
                        { key: "medium", label: "Medium", color: "yellow" },
                        { key: "high", label: "High", color: "orange" },
                        {
                            key: "prohibited",
                            label: "Prohibited",
                            color: "red",
                        },
                    ] as const
                ).map((b) => (
                    <Grid gap="xs" key={b.key} mb={6}>
                        <Grid.Col span={2}>
                            <Group gap="xs" h="100%" align="center">
                                <Badge color={b.color} variant="light">
                                    {b.label}
                                </Badge>
                            </Group>
                        </Grid.Col>
                        <Grid.Col span={5}>
                            <NumberInput
                                label="Min"
                                min={0}
                                max={999}
                                step={0.01}
                                decimalScale={2}
                                value={form.buckets[b.key].min}
                                onChange={(v) =>
                                    updateBucket(b.key, { min: Number(v) })
                                }
                            />
                        </Grid.Col>
                        <Grid.Col span={5}>
                            <NumberInput
                                label="Max"
                                min={0}
                                max={999}
                                step={0.01}
                                decimalScale={2}
                                value={form.buckets[b.key].max}
                                onChange={(v) =>
                                    updateBucket(b.key, { max: Number(v) })
                                }
                            />
                        </Grid.Col>
                    </Grid>
                ))}
            </Card>

            <Card withBorder radius="sm" p="md">
                <Group justify="space-between" mb="xs">
                    <Box>
                        <Title order={5}>KYC Expiry (years)</Title>
                        <Text size="xs" c="dimmed">
                            Used for CRAM-bound KYCs only. Legacy KYCs keep
                            their historical schedule.
                        </Text>
                    </Box>
                </Group>
                <Divider mb="sm" />
                <Grid gap="xs">
                    <Grid.Col span={{ base: 12, md: 4 }}>
                        <NumberInput
                            label="Low"
                            min={1}
                            max={10}
                            step={1}
                            value={form.expiry.low_years}
                            onChange={(v) =>
                                update("expiry", { low_years: Number(v) })
                            }
                        />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 4 }}>
                        <NumberInput
                            label="Medium"
                            min={1}
                            max={10}
                            step={1}
                            value={form.expiry.medium_years}
                            onChange={(v) =>
                                update("expiry", {
                                    medium_years: Number(v),
                                })
                            }
                        />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 4 }}>
                        <NumberInput
                            label="High"
                            min={1}
                            max={10}
                            step={1}
                            value={form.expiry.high_years}
                            onChange={(v) =>
                                update("expiry", { high_years: Number(v) })
                            }
                        />
                    </Grid.Col>
                </Grid>
            </Card>

            <Card withBorder radius="sm" p="md">
                <Group justify="space-between" mb="xs">
                    <Box>
                        <Title order={5}>Feature Flags</Title>
                        <Text size="xs" c="dimmed">
                            Controls the dark-launch of the CRAM pipeline.
                            Access to this portal is assigned separately under
                            Settings → Claims (the CRAM group).
                        </Text>
                    </Box>
                </Group>
                <Divider mb="sm" />
                <Stack gap="sm">
                    <Box>
                        <Text size="sm" fw={500} mb={4}>
                            Rollout mode
                        </Text>
                        <SegmentedControl
                            value={form.mode}
                            onChange={(v) =>
                                setForm((prev) =>
                                    prev ? { ...prev, mode: v as CramMode } : prev
                                )
                            }
                            data={[
                                { label: "Off", value: "off" },
                                { label: "Shadow", value: "shadow" },
                                { label: "Live", value: "live" },
                            ]}
                        />
                        <Text size="xs" c="dimmed" mt={4}>
                            Off = legacy risk only. Shadow = compute & store
                            CRAM for comparison without affecting tags, expiry,
                            or the KYC-fill flow. Live = CRAM drives everything.
                            Rollback = set back to Off.
                        </Text>
                    </Box>
                    <Box>
                        <DateInput
                            label="Activation date"
                            placeholder="No date set — all KYCs eligible"
                            clearable
                            valueFormat="YYYY-MM-DD"
                            value={form.activationDate}
                            onChange={(value) =>
                                setForm((prev) =>
                                    prev
                                        ? {
                                            ...prev,
                                            activationDate:
                                                toLocalMidnight(value),
                                        }
                                        : prev
                                )
                            }
                            maw={260}
                        />
                        <Text size="xs" c="dimmed" mt={4}>
                            KYCs submitted or last updated <b>before</b> this date
                            stay on the legacy risk flow forever (even new-schema
                            KYCs) — CRAM is never computed for them. Only KYCs
                            created or re-submitted/updated on or after it are
                            CRAM-bound. Leave empty for no date gate.
                        </Text>
                    </Box>
                    <Group gap="lg">
                        <Switch
                            label="Enable for Corporate"
                            description="When off, company customers stay on legacy risk (CRAM is not computed or applied)."
                            checked={form.audience.enabledForCorporate}
                            onChange={(e) =>
                                update("audience", {
                                    enabledForCorporate:
                                        e.currentTarget.checked,
                                })
                            }
                        />
                        <Switch
                            label="Enable for Trust"
                            description="When off, trust customers stay on legacy risk (CRAM is not computed or applied)."
                            checked={form.audience.enabledForTrust}
                            onChange={(e) =>
                                update("audience", {
                                    enabledForTrust:
                                        e.currentTarget.checked,
                                })
                            }
                        />
                    </Group>
                    <Alert
                        icon={<IconInfoCircle size={16} />}
                        color="gray"
                        variant="light"
                    >
                        Snapshot version (auto-managed):{" "}
                        <Text span fw={600}>
                            {snapshotVersion ?? "—"}
                        </Text>
                    </Alert>
                </Stack>
            </Card>

            <Group justify="flex-end">
                <Button
                    leftSection={<IconDeviceFloppy size={16} />}
                    onClick={handleSave}
                    loading={saving}
                    disabled={!canEdit || !dirty || !!overlap}
                >
                    Save Settings
                </Button>
            </Group>
        </Stack>
        </Box>
    );
};

export default CramSettingsTab;
